import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import { selectSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { getAuthToken, getGuestModeFromStorage } from "../../helper/helper";
import { canScheduleMatchesInLeague } from "../../utils/leagueAccess";
import { storeTournamentOversInSession } from "../leaguesHome/tournaments/tournamentsDetails/matches/scheduleTournamentContext";
import { startsIn, type MatchVm } from "../tournamentPage/matchVm";
import { Empty, MatchCard, ScheduleCard } from "../tournamentPage/MatchesPanel";
import { IcCalL, IcListL, IcLive, IcTrophy } from "../tournamentPage/icons";
import { norm } from "../teamsTab/teamsData";
import type { TeamCtx } from "./TeamPage";

type Seg = "live" | "up" | "past";
const SEGS: [Seg, string][] = [["live", "Live"], ["up", "Upcoming"], ["past", "Past"]];

const TeamMatchesTab = ({ ctx }: { ctx: TeamCtx }) => {
  const navigate = useNavigate();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const isGuest = useAppSelector((s) => s.authModal.isGuestMode) || getGuestModeFromStorage();
  const canSchedule = Boolean(getAuthToken() && !isGuest && canScheduleMatchesInLeague(selectedLeague));

  const by = useMemo(() => ({
    live: ctx.matches.filter((m) => m.bucket === "live"),
    up: ctx.matches.filter((m) => m.bucket === "up"),
    past: ctx.matches.filter((m) => m.bucket === "past"),
  }), [ctx.matches]);
  const [picked, setPicked] = useState<Seg | null>(null);
  const seg: Seg = picked ?? (by.live.length ? "live" : by.up.length ? "up" : by.past.length ? "past" : "up");
  const list = by[seg];

  const groups = useMemo(() => {
    const sorted = [...list].sort((a, b) => (seg === "past" ? b.ms - a.ms : a.ms - b.ms));
    const out: { key: string; label: string; items: MatchVm[] }[] = [];
    sorted.forEach((m) => {
      const key = m.dayKey || "tba";
      let g = out.find((x) => x.key === key);
      if (!g) { g = { key, label: m.dayKey ? m.dayLabel : "Date to be announced", items: [] }; out.push(g); }
      g.items.push(m);
    });
    return out;
  }, [list, seg]);

  const openSchedule = () => {
    sessionStorage.setItem("tournamentId", ctx.tid);
    sessionStorage.setItem("tournamentName", ctx.tournamentName);
    if (ctx.leagueId) sessionStorage.setItem("leagueId", ctx.leagueId);
    const t = ctx.tournament;
    if (t) {
      storeTournamentOversInSession(t);
      if (t.matchType) sessionStorage.setItem("tournamentMatchType", String(t.matchType));
      if (t.tournamentCategory) sessionStorage.setItem("tournamentCategory", String(t.tournamentCategory));
    }
    navigate("/tournaments/schedule-match");
  };

  const sched = canSchedule ? <ScheduleCard onSchedule={openSchedule} tournamentName={ctx.tournamentName || "this tournament"} /> : null;
  if (ctx.matchesLoading) return <div className="empty"><p>Loading matches…</p></div>;
  if (ctx.matches.length === 0)
    return <>{sched}<Empty icon={<IcCalL />} title="No matches yet" text={`Fixtures for ${ctx.name} haven't been published yet.`} /></>;

  const nextUp = [...by.up].sort((a, b) => a.ms - b.ms)[0];
  const emptySeg = seg === "live"
    ? <Empty icon={<IcLive />} title={`${ctx.name} aren't playing right now`} text="Their live match will show here from the toss." meta={nextUp ? `Next: v ${norm(nextUp.a.name) === norm(ctx.name) ? nextUp.b.name : nextUp.a.name} · ${startsIn(nextUp.start).when}, ${nextUp.time}` : undefined} acts={by.up.length ? <button className="btn primary" onClick={() => setPicked("up")}>See upcoming</button> : undefined} />
    : seg === "up"
      ? <Empty icon={<IcCalL />} title="No upcoming matches" text={`All of ${ctx.name}'s matches are played or live now.`} acts={by.past.length ? <button className="btn primary" onClick={() => setPicked("past")}>See results</button> : undefined} />
      : <Empty icon={by.up.length || by.live.length ? <IcListL /> : <IcTrophy />} title="No results yet" text={`${ctx.name}'s results appear here as soon as each match ends.`} acts={by.up.length ? <button className="btn primary" onClick={() => setPicked("up")}>See fixtures</button> : undefined} />;

  return (
    <>
      {sched}
      <div className="tool tg-tool">
        <div className="seg" role="tablist" aria-label="Match status">
          {SEGS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={seg === k} onClick={() => setPicked(k)}>
              {k === "live" && <span className={`ldot ${by.live.length ? "on" : "off"}`} />}{l}<span className={`ct ${by[k].length ? "" : "z"}`}>{by[k].length}</span>
            </button>
          ))}
        </div>
        <div className="summary">{ctx.name} · {ctx.matches.length} {ctx.matches.length === 1 ? "match" : "matches"} in this tournament</div>
      </div>
      {groups.length === 0 ? emptySeg : groups.map((g) => (
        <div key={g.key}>
          <div className="day"><h3>{g.label}</h3><span>{g.items.length} {g.items.length === 1 ? "match" : "matches"}</span></div>
          <div className="grid">{g.items.map((m) => <MatchCard key={m.id} m={m} viewAs={ctx.name} onOpen={(x) => navigate(`/matches-details/${x.id}`)} />)}</div>
        </div>
      ))}
    </>
  );
};

export default TeamMatchesTab;
